/**
 * The requests to the person's sandbox (the vfs path, `config.vfsServiceUrl`) still waiting for an answer, so Flow can
 * tell a sandbox that is waking (every such request waits in the sandbox gateway until the pod is up) from a page that
 * is just slow. fetchRegistry.js reports every authorized fetch here; only the sandbox's are kept. The wake status
 * endpoints themselves are not counted (they answer at once and are what is asked when something waits).
 *
 * - track(input, promise) -> the same promise; the request is pending until it settles.
 * - slowest(now) -> how long the oldest pending sandbox request has waited (ms), ignoring those older than
 *   STALE_MS (streams and long polls that legitimately stay open); 0 when none.
 * - subscribe(listener) -> unsubscribe; called when a request starts or ends.
 */
import config from '../config';

const STALE_MS = 2 * 60_000;
const IGNORED = /\/api\/v1\/sandbox\/(state|wake)\/?(\?|$)/;
const pending = new Map();
const listeners = new Set();
let nextId = 1;

const urlOf = (input) => {
  if (typeof input === 'string') { return input; }
  if (input && typeof input.url === 'string') { return input.url; }
  return String(input || '');
};

/** A request to the sandbox (the vfs path) that is not a wake status call. */
export function isSandboxRequest(input) {
  const base = String(config.vfsServiceUrl || '').replace(/\/+$/, '');
  if (!base) { return false; }
  const url = urlOf(input);
  return (url === base || url.startsWith(`${base}/`)) && !IGNORED.test(url);
}

function emit() {
  for (const listener of [...listeners]) {
    try { listener(); } catch { /* a listener's problem only */ }
  }
}

export function track(input, promise, now = Date.now) {
  if (!promise || typeof promise.then !== 'function' || !isSandboxRequest(input)) { return promise; }
  const id = nextId;
  nextId += 1;
  pending.set(id, now());
  emit();
  const done = () => { pending.delete(id); emit(); };
  promise.then(done, done);
  return promise;
}

export function slowest(at = Date.now()) {
  let oldest = 0;
  for (const since of pending.values()) {
    const waited = at - since;
    if (waited < STALE_MS && waited > oldest) { oldest = waited; }
  }
  return oldest;
}

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Tests only. */
export function resetPending() {
  pending.clear();
  listeners.clear();
}
